import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { CURRENCIES, CODE_MAX_DIGITS, CODE_MIN_DIGITS, DEMO_CODE_DIGITS } from '@/config/withdrawal';
import type { CodeLink } from '@/hooks/useAdminData';

type CodeLinkCardProps = {
  codeLink: CodeLink | null;
  onSave: (patch: Partial<CodeLink>) => Promise<boolean>;
};

/** Jeton aléatoire (32 caractères hexadécimaux) utilisé dans le lien. */
function generateToken(): string {
  try {
    const uuid = globalThis.crypto?.randomUUID?.();
    if (uuid) return uuid.replace(/[^a-f0-9]/gi, '').slice(0, 32);
  } catch {
    /* crypto indisponible, on retombe sur un jeton basé sur le temps */
  }
  return `${Date.now().toString(16)}${Math.random().toString(16).slice(2)}`.slice(0, 32);
}

function buildPublicUrl(token: string): string {
  const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
  const pathPrefix = basePath ? `/${basePath}` : '';
  return `${window.location.origin}${pathPrefix}/code?token=${token}`;
}

/** Longueurs de code proposées (de 4 à 8 chiffres). */
const DIGIT_OPTIONS = Array.from(
  { length: CODE_MAX_DIGITS - CODE_MIN_DIGITS + 1 },
  (_, index) => CODE_MIN_DIGITS + index,
);

export default function CodeLinkCard({ codeLink, onSave }: CodeLinkCardProps) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('XPF');
  const [digits, setDigits] = useState<number>(DEMO_CODE_DIGITS);
  const [savingAmount, setSavingAmount] = useState(false);
  const [savingDigits, setSavingDigits] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (codeLink) {
      setAmount(String(codeLink.amount ?? 0));
      setCurrency(codeLink.currency || 'XPF');
      setDigits(codeLink.codeDigits || DEMO_CODE_DIGITS);
    }
  }, [codeLink]);

  const active = codeLink?.active ?? false;
  const publicUrl = codeLink?.token ? buildPublicUrl(codeLink.token) : '';

  const handleCopy = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setFeedback({ kind: 'error', text: t('admin.codeLink.copyError') });
    }
  };

  const handleToggle = async () => {
    if (!codeLink) return;
    setToggling(true);
    setFeedback(null);
    const ok = await onSave({ active: !codeLink.active });
    setToggling(false);
    setFeedback(
      ok
        ? { kind: 'ok', text: t('admin.codeLink.statusSaved') }
        : { kind: 'error', text: t('admin.codeLink.saveError') },
    );
  };

  const handleSaveAmount = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSavingAmount(true);
    setFeedback(null);
    const ok = await onSave({
      amount: Number(amount.replace(/[^0-9]/g, '')) || 0,
      currency,
    });
    setSavingAmount(false);
    setFeedback(
      ok
        ? { kind: 'ok', text: t('admin.codeLink.amountSaved') }
        : { kind: 'error', text: t('admin.codeLink.saveError') },
    );
  };

  const handleSaveDigits = async (value: number) => {
    if (!codeLink || value === codeLink.codeDigits) return;
    setSavingDigits(true);
    setFeedback(null);
    const ok = await onSave({ codeDigits: value });
    setSavingDigits(false);
    if (ok) setDigits(value);
    setFeedback(
      ok
        ? { kind: 'ok', text: t('admin.codeLink.digitsSaved') }
        : { kind: 'error', text: t('admin.codeLink.saveError') },
    );
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    setFeedback(null);
    const ok = await onSave({ token: generateToken() });
    setRegenerating(false);
    setFeedback(
      ok
        ? { kind: 'ok', text: t('admin.codeLink.regenerated') }
        : { kind: 'error', text: t('admin.codeLink.saveError') },
    );
  };

  const inputClass =
    'w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100';

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
            <i className="ri-link-m text-lg text-primary-700"></i>
            {t('admin.codeLink.title')}
          </h2>
          <p className="mt-2 max-w-xl text-sm leading-relaxed text-foreground-600">
            {t('admin.codeLink.subtitle')}
          </p>
        </div>
        <span
          className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold ${
            active ? 'bg-primary-100 text-primary-900' : 'bg-secondary-100 text-secondary-900'
          }`}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${active ? 'bg-primary-500' : 'bg-secondary-500'}`}
          ></span>
          {active ? t('admin.codeLink.statusActive') : t('admin.codeLink.statusInactive')}
        </span>
      </div>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        <button
          type="button"
          onClick={() => void handleToggle()}
          disabled={toggling || !codeLink}
          className={`inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
            active
              ? 'border border-background-300 text-foreground-700 hover:bg-background-100'
              : 'bg-primary-500 text-background-50 hover:bg-primary-600 dark:text-foreground-950'
          }`}
        >
          <i className={toggling ? 'ri-loader-4-line animate-spin' : active ? 'ri-toggle-fill' : 'ri-toggle-line'}></i>
          {active ? t('admin.codeLink.disable') : t('admin.codeLink.enable')}
        </button>

        <form onSubmit={handleSaveAmount} className="flex flex-1 flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <span className="pointer-events-none absolute left-3.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-money-dollar-circle-line text-lg"></i>
            </span>
            <input
              type="text"
              inputMode="numeric"
              value={amount}
              onChange={(event) => setAmount(event.target.value.replace(/[^0-9]/g, ''))}
              placeholder={t('admin.codeLink.amountPh')}
              className={`${inputClass} pl-11`}
            />
          </div>
          <div className="relative sm:w-32">
            <span className="pointer-events-none absolute left-3.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center text-foreground-400">
              <i className="ri-coin-line text-lg"></i>
            </span>
            <select
              value={currency}
              onChange={(event) => setCurrency(event.target.value)}
              aria-label={t('admin.codeLink.currency')}
              className={`${inputClass} cursor-pointer pl-11`}
            >
              {CURRENCIES.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            disabled={savingAmount}
            className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-secondary-600 disabled:cursor-not-allowed disabled:opacity-60 dark:text-foreground-950"
          >
            <i className={savingAmount ? 'ri-loader-4-line animate-spin' : 'ri-save-3-line'}></i>
            {t('admin.codeLink.amountSave')}
          </button>
        </form>
      </div>
      <p className="mt-2 text-xs text-foreground-500">{t('admin.codeLink.amountHint')}</p>

      <div className="mt-6 border-t border-background-200 pt-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <span className="flex items-center gap-2 text-sm font-semibold text-foreground-800">
              <i className="ri-hashtag text-base text-primary-700"></i>
              {t('admin.codeLink.digitsLabel')}
            </span>
            <p className="mt-1 max-w-md text-xs text-foreground-500">
              {t('admin.codeLink.digitsHint')}
            </p>
          </div>
          <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
            {DIGIT_OPTIONS.map((value) => (
              <button
                key={value}
                type="button"
                disabled={savingDigits || !codeLink}
                onClick={() => void handleSaveDigits(value)}
                className={`flex h-8 w-9 cursor-pointer items-center justify-center rounded-full text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                  digits === value
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:bg-background-200'
                }`}
              >
                {value}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 border-t border-background-200 pt-5">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm font-semibold text-foreground-800">
            {t('admin.codeLink.linkLabel')}
          </span>
          <button
            type="button"
            onClick={() => void handleRegenerate()}
            disabled={regenerating || !codeLink}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-2.5 py-1.5 text-xs font-semibold text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <i className={regenerating ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'}></i>
            {t('admin.codeLink.regenerate')}
          </button>
        </div>

        <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="flex-1 overflow-x-auto rounded-md border border-background-200 bg-background-100 px-3.5 py-2.5">
            <code className="whitespace-nowrap text-xs text-foreground-800">
              {publicUrl || t('admin.codeLink.linkEmpty')}
            </code>
          </div>
          <button
            type="button"
            onClick={() => void handleCopy()}
            disabled={!publicUrl}
            className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 dark:text-foreground-950"
          >
            <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
            {copied ? t('admin.codeLink.copied') : t('admin.codeLink.copy')}
          </button>
        </div>
        <p className="mt-2 text-xs text-foreground-500">{t('admin.codeLink.linkHint')}</p>
      </div>

      {feedback && (
        <div
          className={`mt-4 flex items-start gap-2 rounded-md border px-3 py-2.5 text-sm ${
            feedback.kind === 'ok'
              ? 'border-primary-200 bg-primary-50 text-primary-900'
              : 'border-accent-200 bg-accent-50 text-accent-900'
          }`}
        >
          <i
            className={`mt-0.5 ${
              feedback.kind === 'ok' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'
            }`}
          ></i>
          <span>{feedback.text}</span>
        </div>
      )}
    </div>
  );
}